<template>
  <v-app-bar theme="dark" height="50" color="#002E5D" extension-height="42">
    <v-btn
      icon="$menu"
      variant="text"
      class="v-app-bar-nav-icon"
      @click="$emit('nav-icon-click')"
    />

    <div id="leftSide">
      <div id="titleBox">
        <div class="text-headline-small">
          <router-link class="text-white no_underline" to="/">{{
            title
          }}</router-link>
        </div>
      </div>
    </div>

    <v-spacer />
    <div>
      <div class="d-flex align-center">
        <v-menu theme="light">
          <template #activator="{ props }">
            <v-btn v-bind="props" class="test-locale mr-2" variant="text">
              <v-icon class="mr-1" size="small">mdi-translate</v-icon>
              {{ selectedLocale.value }}
            </v-btn>
          </template>
          <v-list density="compact">
            <v-list-item
              v-for="(locale, idx) in locales"
              :key="idx"
              class="pa-0"
            >
              <v-btn width="100%" @click="setLocale(locale)" variant="text"
                ><v-icon
                  class="mr-2"
                  :color="
                    selectedLocale.value === locale.value
                      ? 'black'
                      : 'transparent'
                  "
                  size="small"
                  >mdi-check</v-icon
                >{{ locale.value.toUpperCase() }} - {{ locale.label }}</v-btn
              >
            </v-list-item>
          </v-list>
        </v-menu>
        <div v-if="isDevelopmentEnvironment" class="mr-5 test-dev-indicator">
          <v-menu theme="light" open-on-hover>
            <template #activator="{ props }">
              <v-btn v-bind="props" color="info" variant="outlined"
                >DEVELOPMENT</v-btn
              >
            </template>
            <v-card class="pa-5" width="290">
              <v-row class="text-center ma-0 justify-center">
                <span
                  >The website is currently running in a development
                  environment. Changes made will not affect data in the
                  production database. This environment is meant only for web
                  developers who are currently building the site.
                </span>
                <span class="mt-4"
                  >If you are not an OARE developer or this appears when
                  visiting the production website, please contact us immediately
                  at</span
                >
                <a href="mailto:oarefeedback@byu.edu">oarefeedback@byu.edu</a>
              </v-row>
            </v-card>
          </v-menu>
        </div>
        <div v-if="isReadOnlyEnvironment" class="mr-5">
          <v-menu theme="light" open-on-hover>
            <template #activator="{ props }">
              <v-btn v-bind="props" color="red" variant="outlined"
                >SANDBOX DATABASE</v-btn
              >
            </template>
            <v-card class="pa-5" width="290">
              <v-row class="text-center ma-0 justify-center">
                <span
                  >The website is currently running in a development environment
                  connected to a remote database (DB_SOURCE=readonly), normally
                  the shared sandbox. Edits are saved to that database. While in
                  this mode, the cache is disabled for all routes in order to
                  prevent stale data inconsistencies. This environment is meant
                  only for web developers who are currently building the site
                  and need to test it with real data.
                </span>
                <span class="mt-4"
                  >If you are not an OARE developer or this appears when
                  visiting the production website, please contact us immediately
                  at</span
                >
                <a href="mailto:oarefeedback@byu.edu">oarefeedback@byu.edu</a>
              </v-row>
            </v-card>
          </v-menu>
        </div>
        <v-badge
          v-if="isAdmin"
          :model-value="displayAdminBadge"
          color="error"
          dot
          location="top start"
          class="test-admin-badge"
        >
          <v-btn
            v-if="isAdmin"
            class="mr-2 test-admin-btn"
            to="/admin/groups"
            variant="text"
            >Admin</v-btn
          >
        </v-badge>
        <v-btn
          v-if="!isAuthenticated"
          class="test-login-btn"
          to="/login"
          variant="text"
          >{{ $t('appBar.login') }}</v-btn
        >
        <v-menu v-else theme="light">
          <template #activator="{ props }">
            <v-btn v-bind="props" variant="text">
              {{ $t('appBar.welcome') }},
              {{ firstName }}
            </v-btn>
          </template>
          <v-list>
            <v-list-item @click="$router.push('/dashboard/profile')">
              <v-list-item-title>Dashboard</v-list-item-title>
            </v-list-item>
            <v-list-item @click="logout">
              <v-list-item-title>{{ $t('appBar.logout') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>

    <template #extension>
      <v-row class="d-flex justify-center my-1">
        <v-menu theme="light" open-on-hover>
          <template #activator="{ props }">
            <v-btn class="test-texts" v-bind="props" variant="text"
              >Texts</v-btn
            >
          </template>
          <v-list density="compact">
            <v-list-item class="pa-0">
              <v-btn to="/collections/A-J" width="100%" variant="text"
                >By Collection</v-btn
              >
            </v-list-item>
            <v-list-item class="pa-0">
              <v-btn to="/publications/A" width="100%" variant="text"
                >By Publication</v-btn
              >
            </v-list-item>
            <v-list-item class="pa-0">
              <v-btn to="/archives" width="100%" variant="text"
                >By Archive</v-btn
              >
            </v-list-item>
          </v-list>
        </v-menu>
        <v-menu
          theme="light"
          open-on-hover
          v-if="canViewWords || canViewNames || canViewPlaces"
        >
          <template #activator="{ props }">
            <v-btn class="test-lexica" v-bind="props" variant="text">
              Lexica
            </v-btn>
          </template>
          <v-list density="compact">
            <v-list-item v-if="canViewWords" class="pa-0">
              <v-btn
                class="test-words"
                to="/words/A"
                width="100%"
                variant="text"
                >Words</v-btn
              >
            </v-list-item>
            <v-list-item v-if="canViewNames" class="pa-0">
              <v-btn
                class="test-names"
                to="/names/A"
                width="100%"
                variant="text"
                >Names</v-btn
              >
            </v-list-item>
            <v-list-item v-if="canViewPlaces" class="pa-0">
              <v-btn
                class="test-places"
                to="/places/A"
                width="100%"
                variant="text"
                >Places</v-btn
              >
            </v-list-item>
          </v-list>
        </v-menu>

        <v-menu
          v-if="canViewPeriods || canViewPersons"
          theme="light"
          open-on-hover
        >
          <template #activator="{ props }">
            <v-btn class="test-realia" v-bind="props" variant="text">
              Realia
            </v-btn>
          </template>
          <v-list density="compact">
            <v-list-item v-if="canViewPersons" class="pa-0">
              <v-btn
                class="test-places"
                to="/persons/A"
                width="100%"
                variant="text"
                >Persons</v-btn
              >
            </v-list-item>
            <v-list-item v-if="canViewPeriods" class="pa-0">
              <v-btn
                class="test-periods"
                to="/periods"
                width="100%"
                variant="text"
                >Periods</v-btn
              >
            </v-list-item>
          </v-list>
        </v-menu>

        <v-btn class="test-search" to="/search/texts" variant="text"
          >Search</v-btn
        >

        <v-menu theme="light" open-on-hover>
          <template #activator="{ props }">
            <v-btn class="test-misc" v-bind="props" variant="text">
              Misc.
            </v-btn>
          </template>
          <v-list density="compact">
            <v-list-item v-if="canViewBibliography" class="pa-0">
              <v-btn to="/bibliography" width="100%" variant="text"
                >Bibliography</v-btn
              >
            </v-list-item>
            <v-list-item class="pa-0">
              <v-btn to="/about" width="100%" variant="text">About</v-btn>
            </v-list-item>
            <v-list-item class="pa-0">
              <v-btn to="/tutorial" width="100%" variant="text">Tutorial</v-btn>
            </v-list-item>
            <v-list-item class="pa-0">
              <v-btn to="/signList" width="100%" variant="text"
                >Sign List</v-btn
              >
            </v-list-item>
            <v-list-item v-if="canViewSeals" class="pa-0">
              <v-btn to="/seals" width="100%" variant="text">Seals</v-btn>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-row>
    </template>
  </v-app-bar>
</template>

<script lang="ts">
import { defineComponent, computed, ref, onMounted } from 'vue';
import { useDisplay } from 'vuetify';
import i18n from '@/i18n';
import sl from '@/serviceLocator';
import { Locale } from '@oare/types';

export default defineComponent({
  name: 'OareAppBar',
  emits: ['nav-icon-click'],
  setup() {
    const display = useDisplay();
    const actions = sl.get('globalActions');
    const router = sl.get('router');
    const store = sl.get('store');
    const server = sl.get('serverProxy');

    const title = computed(() => {
      if (display.smAndDown.value) {
        return 'OARE';
      }
      return i18n.global.t('appBar.oare');
    });

    const displayAdminBadge = computed(() =>
      Object.values(store.getters.displayAdminBadge).includes(true)
    );

    const isAdmin = computed(() => store.getters.isAdmin);
    const isAuthenticated = computed(() => store.getters.isAuthenticated);
    const firstName = computed(() =>
      store.getters.user ? store.getters.user.firstName : ''
    );
    const isDevelopmentEnvironment = computed(
      () => import.meta.env.MODE === 'development'
    );
    const isReadOnlyEnvironment = ref(false);

    const canViewWords = computed(() => store.hasPermission('WORDS'));
    const canViewNames = computed(() => store.hasPermission('NAMES'));
    const canViewPlaces = computed(() => store.hasPermission('PLACES'));
    const canViewPersons = computed(() => store.hasPermission('PERSONS'));
    const canViewBibliography = computed(() =>
      store.hasPermission('BIBLIOGRAPHY')
    );
    const canViewSeals = computed(() => store.hasPermission('SEALS'));
    const canViewPeriods = computed(() => store.hasPermission('PERIODS'));

    const logout = () => {
      store.logout();
      router.push('/login');
      server.logout();
    };

    const locales = ref<Locale[]>([
      { label: 'English', value: 'en' },
      { label: 'Türkçe', value: 'tr' },
    ]);

    const selectedLocale = ref<Locale>(
      locales.value.find(locale => locale.value === i18n.global.locale.value) ||
        locales.value[0]
    );

    const setLocale = (locale: Locale) => {
      selectedLocale.value = locale;
      localStorage.setItem('locale', selectedLocale.value.value);
      router.go(0);
    };

    onMounted(async () => {
      try {
        isReadOnlyEnvironment.value = await server.getReadOnlyStatus();
      } catch (err) {
        actions.showErrorSnackbar(
          'Error loading environment info',
          err as Error
        );
      }
    });

    return {
      title,
      logout,
      isAdmin,
      isAuthenticated,
      firstName,
      displayAdminBadge,
      isDevelopmentEnvironment,
      isReadOnlyEnvironment,
      canViewWords,
      canViewNames,
      canViewPlaces,
      canViewPersons,
      canViewBibliography,
      canViewSeals,
      canViewPeriods,
      locales,
      selectedLocale,
      setLocale,
    };
  },
});
</script>

<style scoped>
.v-app-bar :deep(.v-btn) {
  --v-btn-size: 0.875rem;
  font-size: var(--v-btn-size);
}

.no_underline {
  text-decoration: none;
}
#leftSide {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: left;
}
#titleBox {
  display: flex;
  flex-direction: column;
  align-content: center;
  margin-left: 10px;
}
</style>
